export function initMiniCart(scope = document) {
  const drawer = document.getElementById('mini-cart-drawer');
  const openButtons = document.querySelectorAll('#js-open-mini-cart, [data-open-mini-cart]');

  if (!drawer || !openButtons.length) return;
  if (drawer.dataset.miniCartReady === 'true') return;

  drawer.dataset.miniCartReady = 'true';

  const drawerContent = drawer.querySelector('.mini-cart__drawer');
  let isUpdating = false;

  function openMiniCart() {
    drawer.classList.add('active');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mini-cart-open');
    updateVatPriceDisplay();
  }

  function closeMiniCart() {
    drawer.classList.remove('active');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mini-cart-open');
  }

  function isCartPage() {
    return window.location.pathname === '/cart' || window.location.pathname === '/cart/';
  }

  function formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    const isFrench = (document.documentElement.lang || window.Shopify?.locale || '').toLowerCase().startsWith('fr');
    if (isFrench) {
      const numStr = value.toFixed(2).replace('.', ',');
      return `${numStr} €`;
    }
    let formatted = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);

    formatted = formatted.replace(',00', ',-').replace('.00', ',-');
    return formatted;
  }

  async function getCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }

    return response.json();
  }

  async function renderMiniCart(openAfterRender = false) {
    try {
      const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
      const fetchUrl = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
      const response = await fetch(`${fetchUrl}?sections=mini-cart-default&v=` + Date.now(), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Mini cart render failed: ${response.status}`);
      }

      const data = await response.json();

      if (data['mini-cart-default'] && drawerContent) {
        drawerContent.innerHTML = data['mini-cart-default'];

        if (window.SufioVAT && typeof window.SufioVAT.onCart === 'function') {
          try {
            window.SufioVAT.onCart({ selectors: '#mini-cart-vat-form', saveButtonEnabled: true });
          } catch (e) {
            console.warn('Failed to re-initialize SufioVAT in mini-cart:', e);
          }
        }
        initVatFormObserver();
        updateVatPriceDisplay(drawerContent);
      }

      if (openAfterRender) {
        openMiniCart();
      }
    } catch (error) {
      console.error('Mini cart render error:', error);
    }
  }

  function updateCartCount(cart) {
    const badges = document.querySelectorAll('.js-cart-count');

    badges.forEach((badge) => {
      if (cart.item_count > 0) {
        badge.textContent = cart.item_count;
        badge.style.display = '';
      } else {
        badge.textContent = '0';
        badge.style.display = 'none';
      }
    });
  }

  function isVatExempt() {
    const vatForm = document.getElementById('mini-cart-vat-form') || document.getElementById('cart-page-vat-form');
    if (vatForm) {
      const numberInput = vatForm.querySelector('.sufio-vat-number-input');
      if (numberInput && numberInput.style.display !== 'none') {
        return false;
      }

      const text = (vatForm.textContent || '').toLowerCase();
      if (text && text.trim().length > 0) {
        const isInvalidOrFailed =
          text.includes('invalid') ||
          text.includes('ongeldig') ||
          text.includes('invalide') ||
          text.includes('not tax-exempt') ||
          text.includes('niet in aanmerking') ||
          text.includes('non exonéré') ||
          text.includes('failed') ||
          text.includes('mislukt') ||
          text.includes('échoué') ||
          text.includes('validating') ||
          text.includes('gecontroleerd') ||
          text.includes('en cours') ||
          text.includes('error') ||
          text.includes('fout');

        if (isInvalidOrFailed) {
          return false;
        }

        const hasSuccessClass = Boolean(
          vatForm.querySelector('.sufio-vat-exempt, .sufio-vat-registered, .sufio-vat-success, .sufio-vat-valid')
        );

        const hasSuccessText =
          text.includes('exempt') ||
          text.includes('vrijgesteld') ||
          text.includes('exonéré') ||
          text.includes('validated') ||
          text.includes('gevalideerd') ||
          text.includes('validé') ||
          text.includes('registered') ||
          text.includes('toegepast') ||
          text.includes('appliqué');

        if (hasSuccessClass || hasSuccessText) {
          return true;
        }
      }
    }

    let storedVat = (window.SufioVAT && typeof window.SufioVAT.getVatNumber === 'function' && window.SufioVAT.getVatNumber())
      || localStorage.getItem('sufio-vat-number');

    if (typeof storedVat !== 'string' || storedVat === 'undefined' || storedVat === 'null') {
      storedVat = '';
    }

    if (storedVat && storedVat.trim().length >= 4) {
      return true;
    }

    return false;
  }

  function getDisplayPrice(cents) {
    const isExempt = isVatExempt();
    const rawCents = Number(cents || 0);
    const finalCents = isExempt ? Math.round(rawCents / 1.21) : rawCents;
    return formatMoney(finalCents);
  }

  function updateVatPriceDisplay(root = drawerContent) {
    if (!root) return;

    const isExempt = isVatExempt();

    // 1. Line Item prices
    const itemPrices = root.querySelectorAll('.mini-cart__price[data-final-price]');
    itemPrices.forEach((el) => {
      const grossCents = Number(el.getAttribute('data-final-price') || 0);
      el.textContent = getDisplayPrice(grossCents);
    });

    const oldPrices = root.querySelectorAll('.mini-cart__price-old[data-compare-price]');
    oldPrices.forEach((el) => {
      const grossCents = Number(el.getAttribute('data-compare-price') || 0);
      if (grossCents > 0 && el.style.display !== 'none') {
        el.textContent = getDisplayPrice(grossCents);
      }
    });

    // 2. Upsell product prices
    const upsellPrices = root.querySelectorAll('.mini-cart__upsell-price[data-final-price]');
    upsellPrices.forEach((el) => {
      const grossCents = Number(el.getAttribute('data-final-price') || 0);
      el.textContent = getDisplayPrice(grossCents);
    });
    const upsellOldPrices = root.querySelectorAll('.mini-cart__upsell-price-old[data-compare-price]');
    upsellOldPrices.forEach((el) => {
      const grossCents = Number(el.getAttribute('data-compare-price') || 0);
      if (grossCents > 0 && el.style.display !== 'none') {
        el.textContent = getDisplayPrice(grossCents);
      }
    });

    // 3. Combine product prices
    const combinePrices = root.querySelectorAll('.mini-cart__combine-price[data-final-price]');
    combinePrices.forEach((el) => {
      const grossCents = Number(el.getAttribute('data-final-price') || 0);
      el.textContent = getDisplayPrice(grossCents);
    });
    const combineOldPrices = root.querySelectorAll('.mini-cart__combine-price-old[data-compare-price]');
    combineOldPrices.forEach((el) => {
      const grossCents = Number(el.getAttribute('data-compare-price') || 0);
      if (grossCents > 0 && el.style.display !== 'none') {
        el.textContent = getDisplayPrice(grossCents);
      }
    });

    // 4. Cart Total
    const totalRow = root.querySelector('.mini-cart__row--total');
    if (totalRow) {
      const totalValSpan = totalRow.querySelector('span:last-child');
      const grossTotal = Number(totalRow.getAttribute('data-total-price') || 0);
      if (totalValSpan && grossTotal > 0) {
        totalValSpan.textContent = getDisplayPrice(grossTotal);
      }

      let subtext = totalRow.querySelector('.mini-cart__vat-subtext');
      if (isExempt) {
        if (!subtext) {
          subtext = document.createElement('span');
          subtext.className = 'mini-cart__vat-subtext text-second fw-normal';
          subtext.style.fontSize = '0.75em';
          subtext.style.marginLeft = '6px';
          subtext.style.opacity = '0.85';
          const labelSpan = totalRow.querySelector('span:first-child');
          if (labelSpan) {
            labelSpan.appendChild(subtext);
          }
        }
        subtext.textContent = '(excl. BTW)';
        subtext.style.display = 'inline';
      } else if (subtext) {
        subtext.style.display = 'none';
      }
    }
  }

  function updateFooterTotals(root, cart) {
    if (!root) return;

    const totalRow = root.querySelector('.mini-cart__row--total');
    if (totalRow) {
      totalRow.setAttribute('data-total-price', cart.total_price);
      const totalValue = totalRow.querySelector('span:last-child');
      if (totalValue) {
        totalValue.textContent = getDisplayPrice(cart.total_price);
      }
    }

    const discountsWrap = root.querySelector('.mini-cart__discounts');
    if (discountsWrap) {
      discountsWrap.innerHTML = '';

      if (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length) {
        cart.cart_level_discount_applications.forEach((discount) => {
          const row = document.createElement('div');
          row.className = 'mini-cart__row mini-cart__row--discount';
          row.innerHTML = `
            <span>${discount.title}</span>
            <span>-${formatMoney(discount.total_allocated_amount)}</span>
          `;
          discountsWrap.appendChild(row);
        });
      }
    }

    updateVatPriceDisplay(root);
  }

  function syncUpsellCheckboxes(root, cart) {
    if (!root) return;

    const upsellToggles = root.querySelectorAll('[data-upsell-toggle]');

    upsellToggles.forEach((toggle) => {
      const variantId = Number(toggle.getAttribute('data-variant-id') || 0);
      if (!variantId) return;

      const existsInCart = cart.items.some((item) => Number(item.variant_id) === variantId);
      toggle.checked = existsInCart;
    });
  }

  function updateMiniCartDOM(cart) {
    if (!drawerContent) return;

    const root = drawerContent.querySelector('#mini-cart-root') || drawerContent;
    const itemsWrap = root.querySelector('.mini-cart__items');

    updateFooterTotals(root, cart);
    syncUpsellCheckboxes(root, cart);

    if (!itemsWrap) return;

    const currentItems = Array.from(itemsWrap.querySelectorAll('.mini-cart__item'));

    currentItems.forEach((itemEl) => {
      const key = itemEl.getAttribute('data-cart-item-key');
      const cartItem = cart.items.find((item) => item.key === key);

      if (!cartItem || cartItem.quantity <= 0) {
        itemEl.remove();
        return;
      }

      const qtyInput = itemEl.querySelector('.mini-cart__qty-input');
      const qtyButtons = itemEl.querySelectorAll('[data-qty-change]');
      const priceEl = itemEl.querySelector('.mini-cart__price');
      const oldPriceEl = itemEl.querySelector('.mini-cart__price-old');
      const removeBtn = itemEl.querySelector('[data-remove-item]');

      if (qtyInput) {
        qtyInput.value = cartItem.quantity;
        qtyInput.setAttribute('data-key', cartItem.key);
      }

      qtyButtons.forEach((btn) => {
        const label = (btn.getAttribute('aria-label') || '').toLowerCase();
        btn.setAttribute('data-key', cartItem.key);

        if (label.includes('decrease')) {
          btn.setAttribute('data-quantity', Math.max(0, cartItem.quantity - 1));
        } else if (label.includes('increase')) {
          btn.setAttribute('data-quantity', cartItem.quantity + 1);
        }
      });

      if (removeBtn) {
        removeBtn.setAttribute('data-key', cartItem.key);
      }

      if (priceEl) {
        priceEl.setAttribute('data-final-price', cartItem.final_price);
        priceEl.textContent = getDisplayPrice(cartItem.final_price);
      }

      if (oldPriceEl) {
        const comparePriceFromDom = Number(oldPriceEl.getAttribute('data-compare-price') || 0);

        if (comparePriceFromDom > cartItem.final_price) {
          oldPriceEl.textContent = getDisplayPrice(comparePriceFromDom);
          oldPriceEl.style.display = '';
        } else {
          oldPriceEl.textContent = '';
          oldPriceEl.style.display = 'none';
        }
      }
    });

    updateVatPriceDisplay(root);

    if (cart.item_count === 0) {
      renderMiniCart();
    }
  }

  async function syncAndRender(openAfterRender = false) {
    try {
      const cart = await getCart();
      updateCartCount(cart);
      updateMiniCartDOM(cart);
      await renderMiniCart(openAfterRender);
      return cart;
    } catch (error) {
      console.error('Mini cart sync error:', error);
      return null;
    }
  }

  async function afterCartMutation(openAfterRender = true) {
    const cart = await syncAndRender(openAfterRender);

    document.dispatchEvent(
      new CustomEvent('cart:refresh-complete', {
        detail: cart
      })
    );

    if (isCartPage()) {
      window.location.reload();
      return;
    }

    return cart;
  }

  async function updateCart(itemKey, quantity) {
    if (isUpdating) return;

    isUpdating = true;
    drawer.classList.add('is-loading');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: Math.max(0, Number(quantity))
        })
      });

      if (!response.ok) {
        throw new Error(`Mini cart update failed: ${response.status}`);
      }

      await afterCartMutation(true);
    } catch (error) {
      console.error('Mini cart update error:', error);
      await renderMiniCart();
    } finally {
      isUpdating = false;
      drawer.classList.remove('is-loading');
    }
  }

  async function addUpsell(variantId) {
    if (isUpdating) return;

    isUpdating = true;
    drawer.classList.add('is-loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error(`Upsell add failed: ${response.status}`);
      }

      await afterCartMutation(true);
    } catch (error) {
      console.error('Upsell add error:', error);
      await renderMiniCart();
    } finally {
      isUpdating = false;
      drawer.classList.remove('is-loading');
    }
  }

  async function addCombineProduct(variantId, button = null) {
    if (isUpdating) return;

    isUpdating = true;
    drawer.classList.add('is-loading');

    if (button) {
      button.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error(`Combine add failed: ${response.status}`);
      }

      await afterCartMutation(true);
    } catch (error) {
      console.error('Combine add error:', error);
      await renderMiniCart();
    } finally {
      isUpdating = false;
      drawer.classList.remove('is-loading');

      if (button) {
        button.disabled = false;
      }
    }
  }

  async function removeUpsellByVariantId(variantId) {
    if (isUpdating) return;

    isUpdating = true;
    drawer.classList.add('is-loading');

    try {
      const cart = await getCart();
      const lineItem = cart.items.find((item) => Number(item.variant_id) === Number(variantId));

      if (!lineItem) {
        await renderMiniCart();
        return;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: lineItem.key,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error(`Upsell remove failed: ${response.status}`);
      }

      await afterCartMutation(true);
    } catch (error) {
      console.error('Upsell remove error:', error);
      await renderMiniCart();
    } finally {
      isUpdating = false;
      drawer.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', async function (event) {
    const openTrigger = event.target.closest('#js-open-mini-cart, [data-open-mini-cart]');
    if (openTrigger) {
      event.preventDefault();
      openMiniCart();
      drawer.classList.add('is-loading');
      await syncAndRender(false);
      drawer.classList.remove('is-loading');
      return;
    }

    const openVatTrigger = event.target.closest('[data-open-vat]');
    if (openVatTrigger) {
      event.preventDefault();
      const sufioLink = document.querySelector('#mini-cart-vat-form .sufio-vat-link');
      if (sufioLink) {
        sufioLink.click();
      }
      return;
    }

    const combineAddButton = event.target.closest('[data-combine-add]');
    if (combineAddButton) {
      event.preventDefault();

      if (isUpdating) return;

      const variantId = combineAddButton.getAttribute('data-variant-id');
      if (!variantId) return;

      await addCombineProduct(variantId, combineAddButton);
      return;
    }

    const closeTrigger = event.target.closest('[data-mini-cart-close]');
    if (closeTrigger) {
      event.preventDefault();
      closeMiniCart();
      return;
    }

    const overlayTrigger = event.target.closest('.mini-cart__overlay');
    if (overlayTrigger) {
      event.preventDefault();
      closeMiniCart();
      return;
    }

    const qtyButton = event.target.closest('[data-qty-change]');
    if (qtyButton) {
      event.preventDefault();

      if (isUpdating) return;

      const itemKey = qtyButton.getAttribute('data-key');
      let quantity = Number(qtyButton.getAttribute('data-quantity'));

      if (!itemKey) return;
      if (isNaN(quantity) || quantity < 0) quantity = 0;

      await updateCart(itemKey, quantity);
      return;
    }

    const removeButton = event.target.closest('[data-remove-item]');
    if (removeButton) {
      event.preventDefault();

      if (isUpdating) return;

      const itemKey = removeButton.getAttribute('data-key');
      if (!itemKey) return;

      await updateCart(itemKey, 0);
      return;
    }
  });

  document.addEventListener('change', async function (event) {
    const qtyInput = event.target.closest('.mini-cart__qty-input');
    if (qtyInput) {
      if (isUpdating) return;

      const itemKey = qtyInput.getAttribute('data-key');
      let quantity = Number(qtyInput.value);

      if (!itemKey) return;
      if (isNaN(quantity) || quantity < 0) quantity = 0;

      qtyInput.value = quantity;
      await updateCart(itemKey, quantity);
      return;
    }

    const upsellToggle = event.target.closest('[data-upsell-toggle]');
    if (upsellToggle) {
      if (isUpdating) return;

      const variantId = upsellToggle.getAttribute('data-variant-id');
      if (!variantId) return;

      if (upsellToggle.checked) {
        await addUpsell(variantId);
      } else {
        await removeUpsellByVariantId(variantId);
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && drawer.classList.contains('active')) {
      closeMiniCart();
    }
  });

  document.addEventListener('cart:refresh', async function () {
    await syncAndRender(false);
  });

  document.addEventListener('vat:change', function () {
    updateVatPriceDisplay();
  });

  document.addEventListener('visibilitychange', async function () {
    if (!document.hidden && drawer.classList.contains('active')) {
      await syncAndRender(false);
    }
  });

  function updateVatButtonState() {
    const vatForm = document.getElementById('mini-cart-vat-form');
    const vatButton = document.querySelector('[data-open-vat]');
    if (!vatForm || !vatButton) return;

    // Sanitize Sufio email input silently to prevent validation blocks
    const emailInput = vatForm.querySelector('.sufio-vat-email-input input');
    if (emailInput) {
      emailInput.removeAttribute('required');
      if (emailInput.getAttribute('type') === 'email') {
        emailInput.setAttribute('type', 'text');
      }
      emailInput.setAttribute('autocomplete', 'new-password');
      
      const val = emailInput.value || '';
      const customerEmail = vatForm.getAttribute('data-customer-email') || '';
      const targetEmail = customerEmail ? customerEmail : 'no-email@officegoodsonline.com';
      
      if (!val || !val.includes('@') || val.length < 5) {
        emailInput.value = targetEmail;
      }
    }

    const numberInput = vatForm.querySelector('.sufio-vat-number-input');
    const isFormOpen = numberInput && numberInput.style.display !== 'none';
    
    if (isFormOpen) {
      vatButton.style.setProperty('display', 'none', 'important');
    } else {
      vatButton.style.setProperty('display', '', '');
    }

    updateVatPriceDisplay();
  }



  let vatObserver = null;
  function initVatFormObserver() {
    if (vatObserver) {
      vatObserver.disconnect();
    }
    const vatForm = document.getElementById('mini-cart-vat-form');
    if (!vatForm) return;

    vatObserver = new MutationObserver(updateVatButtonState);
    vatObserver.observe(vatForm, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['style', 'class']
    });
    
    updateVatButtonState();
  }

  initVatFormObserver();
}